// The job's Details tab: the workflow bar (target against actual dates, step by step, with "move here"), the Engineering
// list (what stops the job at the Engineering check and again at the Engineering lock), change orders, the note, and
// Retire / Bring back. Steps, owners and gates are rows edited in Settings. Lock and revisions live on the Engineering
// lock tab (Lock.tsx; docs/job-view-brief.md section 1: "Lock as Rev 0 is reached from the Engineering lock step").

import { Link } from 'react-router-dom';
import { STEP, engineeringBlockers, stepsForJob, undecidedAttributes } from '@zerogap/engine';
import { refresh, rpc, toast, updateRow, useRows } from '../../lib/api';
import { useCadCheck } from '../../lib/cadcheck';
import { useProgress } from '../../lib/progress';
import { longDate, shortDate, today } from '../../lib/format';
import { useSession } from '../../lib/session';
import { moveJobToStep } from '../../lib/steps';
import { Btn, Edit, Empty, Info, Ln, Panel, Tag } from '../../ui/kit';
import { ChangeOrders } from './ChangeOrders';
import type { JobCtx } from './Workspace';
import '../../styles/jobview-details.css';

/** The steps the Engineering gate sits on (docs/job-view-build-notes.md): the Engineering check, and again the Engineering lock. */
export const ENGINEERING_GATES = ['engineering_check', 'engineering_lock'];

export function Details({ ctx }: { ctx: JobCtx }) {
  const { data, r, jobId } = ctx;
  const s = useSession();
  const job = data.job!;
  const res = r.res!;
  const steps = useRows('workflow_step', { order: ['sort'] });
  const dates = useRows('job_step', { eq: { job_id: jobId } });
  const users = useRows('user');
  const canEdit = s.canEdit('screen.jobs');
  const current = steps.rows.find((x) => x.id === job.stage_id);
  const cad = useCadCheck(jobId, r.res, r.lib);
  const progress = useProgress(steps.rows).of(job);
  // the steps this job goes through: a step may belong only to jobs with a certain attribute (the design lane is the lead's)
  const attrs = { ...job, design_lane: data.opportunity?.design_lane ?? null };
  const mine = stepsForJob(steps.rows as any[], attrs);
  const undecided = undecidedAttributes(steps.rows, attrs);
  const shownSteps = current && !mine.some((x) => x.id === current.id) ? [...mine, current].sort((a, b) => a.sort - b.sort) : mine;
  // the Engineering gate (docs/ai-edit-rights-brief.md, 2026-10-22): what an AI changed and nobody verified, and the
  // library content the job prices from that is unverified; the job cannot move past the Engineering check, nor past the
  // Engineering lock, until each is verified
  const scopeRows = useRows('scope_row', { eq: { opportunity_id: job.opportunity_id }, enabled: !!job.opportunity_id });
  const blockers = engineeringBlockers(res, { job: job as any, items: data.items as any[], scopeRows: scopeRows.rows as any[] });
  const gates = steps.rows.filter((x) => ENGINEERING_GATES.includes(x.key as string)).sort((a, b) => Number(a.sort) - Number(b.sort));
  const firstGate = gates[0];
  const who = (id: unknown) => users.rows.find((u) => u.id === id)?.name ?? 'an AI employee';

  const setStep = async (stepId: string) => {
    const step = steps.rows.find((x) => x.id === stepId);
    if (!step) return;
    // the CAD check cannot be left behind while a difference is unresolved
    const cadStep = mine.find((x) => x.key === STEP.cadCheck);
    if (cadStep && step.sort > cadStep.sort && cad.blocker) {
      toast(`This job cannot move past ${cadStep.name}: ${cad.blocker} Open the CAD check tab.`);
      return;
    }
    const passed = gates.find((g) => Number(step.sort) > Number(g.sort));
    if (passed && blockers.length > 0) {
      toast(`This job cannot pass ${passed.name}: ${blockers.length} unverified. Verify or send back each one in the Engineering list below.`);
      return;
    }
    await moveJobToStep(job, steps.rows, stepId);
  };

  const setRetired = (retire: boolean) => {
    if (retire && !window.confirm(`Retire ${job.scope_name}? It counts nowhere from now: not in the lead's value, on no list but Done. Nothing is deleted.`)) return;
    void rpc('retire_job', { p_id: jobId, p_note: null, p_retire: retire }).then(() => refresh('job'));
  };

  return (
    <div className="stack jvd">
      {job.retired_at && (
        <div className="pctl">
          <span className="tag off">retired{job.retired_note ? ` · ${job.retired_note}` : ''} · counts nowhere</span>
          {s.role === 'owner' && canEdit && (
            <Btn small title="Bring it back: it counts again" onClick={() => setRetired(false)}>
              Bring back
            </Btn>
          )}
        </div>
      )}
      <Panel label="Workflow · target and actual dates" right={<Info left>A step exists wherever work changes hands. Steps, owners, gates and lock points are edited in Settings.</Info>}>
        <div style={{ marginBottom: 10 }}>
          <b>{progress.overall_pct}%</b> overall · {progress.steps_done} of {progress.steps_total} steps done
          {progress.cards.cards > 0 && (
            <small>
              {' '}
              · cards: {progress.cards.closed_hours} of {progress.cards.total_hours} standard hours closed, {progress.cards.actual_hours} h actual against them
            </small>
          )}
          <div className="meter">
            <i style={{ width: `${progress.overall_pct}%` }} />
          </div>
        </div>
        <div className="wf">
          <div className="steps">
            {shownSteps.map((step) => {
              const d = dates.rows.find((x) => x.workflow_step_id === step.id);
              const done = !!d?.actual_date;
              const late = d?.target_date && ((d.actual_date && d.actual_date > d.target_date) || (!d.actual_date && d.target_date < today() && (current?.sort ?? 0) <= step.sort));
              return (
                <div key={step.id} className={`st${done ? ' done' : ''}${step.id === job.stage_id ? ' now' : ''}${step.lock_point ? ' lock' : ''}`}>
                  <b>{step.name}</b>
                  <span className="o">{step.owner_role ?? ''}</span>
                  {step.gate && <div className="tag off">gate</div>}
                  {progress.fill[step.key] !== undefined && (
                    <div title={step.key === STEP.build ? `${progress.cards.closed_hours} of ${progress.cards.total_hours} standard hours closed · ${progress.cards.actual_hours} h actual` : `${progress.cards.pulled_hours} of ${progress.cards.total_hours} standard hours pulled`}>
                      <div className="meter">
                        <i style={{ width: `${progress.fill[step.key]}%` }} />
                      </div>
                      <small>
                        {progress.fill[step.key]}%{step.key === STEP.build ? ` · ${progress.cards.actual_hours} h actual vs ${progress.cards.closed_hours} h standard` : ` · ${progress.cards.pulled_hours} of ${progress.cards.total_hours} h`}
                      </small>
                    </div>
                  )}
                  {step.key === STEP.cadCheck && cad.blocker && <div className="tag un">{cad.source ? `${cad.open.length} to resolve` : 'no export yet'}</div>}
                  {ENGINEERING_GATES.includes(step.key) && blockers.length > 0 && <div className="tag un hatched" title="What stops the job here is listed under Engineering below.">{blockers.length} to verify</div>}
                  <div className={`d${late ? ' late' : ''}`}>
                    {shortDate(d?.target_date)} · {d?.actual_date ? shortDate(d.actual_date) : '—'}
                  </div>
                  {canEdit && step.id !== job.stage_id && (
                    <button type="button" className="tag off" style={{ marginTop: 4 }} onClick={() => void setStep(step.id)}>
                      move here
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <small>Each step shows target · actual. The gold bar is where the job is now. Cards pulled fills as cards are pulled, Build as they are closed, both by standard hours.</small>
        {undecided.includes('design_path') && <div className="warn">This job's design path is not chosen yet (Job setup), so the steps that depend on it are not shown.</div>}
      </Panel>

      <Panel
        label={`Engineering · ${blockers.length === 0 ? 'nothing unverified' : `${blockers.length} to verify`}`}
        soft={blockers.length > 0}
        right={
          <Info left>
            The job cannot pass {gates.length ? gates.map((g) => g.name).join(', nor ') : 'the Engineering check'} while anything on it is unverified: a job item, a scope row or the job's setup an AI employee changed, or library content the job prices from. Each row opens its before/after view, where one Verify covers every edit on it, or Send back returns it to the version you verified with a note to the editor.
          </Info>
        }
      >
        {blockers.length === 0 ? (
          <Empty>Everything on this job is verified{firstGate && current && current.sort <= firstGate.sort ? `; it can move past ${firstGate.name}` : ''}.</Empty>
        ) : (
          blockers.map((b) => (
            <Ln
              key={`${b.table}:${b.id}`}
              left={
                <>
                  <Tag kind="un">{b.kind === 'library' ? 'library' : b.kind === 'scope_row' ? 'scope' : b.kind === 'job' ? 'job setup' : 'item'}</Tag> {b.label}
                  {b.kind === 'library' && b.items.length > 0 && <small> · on {b.items.join(', ')}</small>}
                  {b.since && (
                    <>
                      <br />
                      <small>
                        changed by {who(b.by)} · {longDate(b.since)}
                      </small>
                    </>
                  )}
                </>
              }
              right={
                s.can('verify') ? (
                  <Link className="btn sm" to={`/verify/${b.table}/${b.id}`}>
                    Verify
                  </Link>
                ) : (
                  <Tag kind="un">unverified</Tag>
                )
              }
            />
          ))
        )}
      </Panel>

      <ChangeOrders ctx={ctx} />

      <Panel label="Note">
        <Edit className="cell" multiline disabled={!canEdit} value={job.note ?? ''} placeholder="Anything the next person should know" onSave={(v) => updateRow('job', jobId, { note: v || null })} />
      </Panel>
      {s.role === 'owner' && canEdit && !job.retired_at && (
        <small>
          <button type="button" className="linklike" title="A duplicate: it counts nowhere from now, not in the lead's value, on no list but Done. Nothing is deleted." onClick={() => setRetired(true)}>
            Retire this job
          </button>
        </small>
      )}
    </div>
  );
}
